<template>
  <div class="travel-entry">
    <div class="travel-entry__title">
      {{ $t('label') }}
    </div>
    <div class="travel-entry__content">
      <form class="ovp-entry__form" @submit.prevent="submit">
        <div class="travel-entry__wrapper">
          <div class="travel-entry__cell">
            <date-input
              v-model="travelStart"
              class="ovp-entry__field ovp-entry__field--travelStart"
              lazy
              required
              :has-native-datepicker="hasNativeDatepicker"
              :title-label="$t('start-date-label')"
              :errors="
                dateErrors(
                  'travelStart',
                  travelStart,
                  $t('error-message-travel-start-after-travel-end'),
                )
              "
            />
          </div>
          <div class="travel-entry__cell">
            <date-input
              v-model="travelEnd"
              class="ovp-entry__field ovp-entry__field--travelStart"
              lazy
              required
              :has-native-datepicker="hasNativeDatepicker"
              :title-label="$t('end-date-label')"
              :errors="
                dateErrors(
                  'travelEnd',
                  travelEnd,
                  $t('error-message-travel-end-before-travel-start'),
                )
              "
            />
          </div>
          <div class="travel-entry__button">
            <sanitas-button
              class="sanitas-button"
              primary
              full-width-in-xs
              type="submit"
            >
              {{ $t('button-text') }}
            </sanitas-button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { provideI36n, useI36n } from '@jota-one/i36n/src/index.vue2'
import isBefore from 'date-fns/isBefore'
import isSameDay from 'date-fns/isSameDay'
import Vue from 'vue'
import Vuelidate from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

import { formatDate } from '@/helpers/date'
import { isIsoDate } from '@/helpers/validation'

Vue.use(Vuelidate)

export default {
  name: 'SanitasTravelEntryModule',

  components: {
    DateInput: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/DateInput'
      )
    },
    SanitasButton: () => {
      return import(
        /* webpackChunkName: "sanitas-components" */
        '@/components/sanitas-components/SanitasButton'
      )
    },
  },

  validations: {
    travelStart: {
      KEY_REQUIRED: required,
      ISO_DATE_ERROR: isIsoDate,
    },
    travelEnd: {
      KEY_REQUIRED: required,
      ISO_DATE_ERROR: isIsoDate,
    },
  },

  props: {
    travelEntryBaseUrl: {
      type: String,
      default: 'https://egate.sanitas.com/travel/travel.xhtml',
    },
    language: {
      type: String,
      default: 'en',
    },

    discountCode: {
      type: String,
      default: '',
    },
    campaignId: {
      type: String,
      default: '',
    },
    hasNativeDatepicker: {
      type: Boolean,
      default: false,
    },
    showDiscountField: {
      type: String,
      default: '0',
    },
  },
  setup(props) {
    const loadLabels = lang => import(`./i18n/${lang}.json`)
    provideI36n(props.language, { load: loadLabels })
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    travelStart: '',
    travelEnd: '',
    startDateErrors: null,
    endDateErrors: null,
  }),

  computed: {
    /**
     * https://egate.sanitas.com/travel/travel.xhtml?travelStart=01.04.2017&travelEnd=14.04.2017&discountCode=&cmpID=&lang=de&sdf=0
     * @returns {string}
     */
    computeTravelEntryURL() {
      return (
        this.travelEntryBaseUrl +
        '?' +
        'travelStart=' +
        formatDate(this.travelStart) +
        '&' +
        'travelEnd=' +
        formatDate(this.travelEnd) +
        '&' +
        'discountCode=' +
        this.discountCode +
        '&' +
        'cmpID=' +
        this.campaignId +
        '&' +
        'lang=' +
        this.language +
        '&' +
        'sdf=' +
        this.showDiscountField
      )
    },
    formValid() {
      return (
        !this.$v.$invalid &&
        (isBefore(Date.parse(this.travelStart), Date.parse(this.travelEnd)) ||
          isSameDay(
            Date.parse(this.travelStart),
            Date.parse(this.travelEnd),
          )) &&
        isBefore(new Date(), Date.parse(this.travelStart))
      )
    },
  },

  methods: {
    submit() {
      this.reset()
      if (this.formValid) {
        console.log(this.computeTravelEntryURL)
        window.open(this.computeTravelEntryURL).focus()
      }
    },
    dateErrors(key, value, afterError) {
      if (this.$v[key].$invalid && this.$v[key].$dirty) {
        return { errors: this.$t('error-message') }
      }
      if (isBefore(Date.parse(this.travelEnd), Date.parse(this.travelStart))) {
        return { errors: afterError }
      }
      if (isBefore(Date.parse(value), new Date())) {
        return { errors: this.$t('error-message-date-past') }
      }
      return null
    },
    reset() {
      this.$v.$reset()
      this.$v.$touch()
      this.startDateErrors = null
      this.endDateErrors = null
    },
  },
}
</script>

<style scoped>
.travel-entry {
  &__title {
    font-weight: 700;
    font-style: normal;
    font-size: 24px;
    line-height: 1.2;
    color: var(--c-primary-neutral-1);
  }

  &__wrapper {
    display: flex;
    padding: 0 15px 15px 0;
    flex-direction: column;
    @media (--md) {
      flex-direction: row;
    }
  }

  &__cell {
    width: 100%;
    padding: 15px 20px 15px 0;

    &:first-child {
      padding-top: 0;
    }

    @media (--md) {
      width: 35%;
      &:first-child {
        padding-top: 15px;
      }
    }
  }

  &__button {
    margin: 0 auto auto 0;
    padding-top: 20px;
    @media (--md) {
      margin-left: 10px;
    }
  }
}
</style>
